import type { RefObject } from 'react';
import { useEffect } from 'react';

/**
 * Creates a carousel element with specified attributes
 */
const createCarouselElement = (playlistId: string, videoHeight: string, loop: boolean): HTMLElement => {
  const carouselElement = document.createElement('carousel-playlist');
  carouselElement.setAttribute('playlist-id', playlistId);
  carouselElement.setAttribute('video-height', videoHeight);

  if (loop) {
    carouselElement.setAttribute('loop', '');
  }

  return carouselElement;
};

/**
 * Checks if script already exists in the document
 */
const doesScriptExist = (scriptUrl: string): boolean => {
  return Boolean(document.querySelector(`script[src="${scriptUrl}"]`));
};

/**
 * Loads the Lora SDK script and returns cleanup functions
 */
const loadLoraScript = (
  scriptUrl: string,
  onLoadCallback: () => void,
): { scriptElement: HTMLScriptElement; cleanupScript: () => void } => {
  // Create script element
  const scriptElement = document.createElement('script');
  scriptElement.src = scriptUrl;
  scriptElement.async = true;

  // Handle script loading errors
  scriptElement.onerror = (error) => {
    console.error('Failed to load Lora SDK:', error);
  };

  // Execute callback when script is loaded
  scriptElement.onload = () => {
    try {
      onLoadCallback();
    } catch (error) {
      console.error('Error initializing Lora carousel:', error);
    }
  };

  // Append script to document
  document.body.appendChild(scriptElement);

  // Return script removal function
  const cleanupScript = () => {
    if (scriptElement.parentNode) {
      scriptElement.parentNode.removeChild(scriptElement);
    }
  };

  return { scriptElement, cleanupScript };
};

/**
 * Cleans up container contents
 */
const cleanupContainer = (containerElement: HTMLDivElement): void => {
  if (containerElement) {
    containerElement.innerHTML = '';
  }
};

/**
 * Initializes carousel in the container element
 */
const initializeCarousel = (
  containerElement: HTMLDivElement,
  playlistId: string,
  videoHeight: string,
  loop: boolean,
): void => {
  if (!containerElement || containerElement.childElementCount > 0) {
    return;
  }

  const carouselElement = createCarouselElement(playlistId, videoHeight, loop);
  containerElement.appendChild(carouselElement);
};

interface UseLoraCarouselProps {
  containerRef: RefObject<HTMLDivElement>;
  playlistId: string;
  videoHeight: string;
  loop?: boolean;
}

const LORA_SDK_URL = 'https://lora-sdk.belive.sg/embed-js/latest/lora-embed.js';

/**
 * Custom hook for loading Lora SDK and managing carousel elements
 */
export const useLoraCarousel = ({ containerRef, playlistId, videoHeight, loop = true }: UseLoraCarouselProps): void => {
  useEffect(() => {
    const containerElement = containerRef.current;

    // Skip execution if not in browser or container is missing
    if (typeof window === 'undefined' || !containerElement) {
      return;
    }

    const scriptExists = doesScriptExist(LORA_SDK_URL);

    if (!scriptExists) {
      const { scriptElement, cleanupScript } = loadLoraScript(LORA_SDK_URL, () => {
        initializeCarousel(containerElement, playlistId, videoHeight, loop);
      });

      return () => {
        cleanupScript();
        cleanupContainer(containerElement);
      };
    } else if (containerElement.childElementCount === 0) {
      // If script already exists but carousel doesn't, create carousel
      initializeCarousel(containerElement, playlistId, videoHeight, loop);
    }
  }, [containerRef, playlistId, videoHeight, loop]);
};
